import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Send, Clock, CheckCircle2, XCircle, Trash2, Music4 } from 'lucide-react';

const STATUS_META = {
  pending:  { label: 'En attente',  icon: Clock,         color: 'text-amber-300 bg-amber-950/50 border-amber-800/50' },
  approved: { label: 'Approuvée',   icon: CheckCircle2,  color: 'text-emerald-300 bg-emerald-950/50 border-emerald-800/50' },
  rejected: { label: 'Refusée',     icon: XCircle,       color: 'text-red-300 bg-red-950/50 border-red-800/50' },
};

export default function SongRequests() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState(isAdmin ? 'admin' : 'mine');
  const [form, setForm] = useState({ title: '', artist: '', note: '' });
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    const [m, a] = await Promise.all([
      api.get('/song-requests/mine').catch(() => ({ data: [] })),
      isAdmin ? api.get('/song-requests').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setMine(m.data);
    setAll(a.data);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.artist.trim()) { toast.error('Titre et artiste requis'); return; }
    setSaving(true);
    try {
      await api.post('/song-requests', form);
      toast.success('Ta demande a été envoyée !');
      setForm({ title: '', artist: '', note: '' });
      refresh();
    } catch { toast.error('Impossible d\'envoyer la demande'); }
    finally { setSaving(false); }
  };

  const resolve = async (id, status) => {
    const admin_reply = status === 'rejected'
      ? window.prompt('Note pour l\'utilisateur (optionnel) :', '')
      : null;
    try {
      await api.patch(`/song-requests/${id}`, { status, admin_reply });
      toast.success(status === 'approved' ? 'Approuvée' : 'Refusée');
      refresh();
    } catch { toast.error('Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await api.delete(`/song-requests/${id}`);
      refresh();
    } catch { toast.error('Erreur'); }
  };

  const pending = all.filter(r => r.status === 'pending');
  const resolved = all.filter(r => r.status !== 'pending');

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-4xl">
        <div className="mb-8 rise">
          <div className="overline text-orange-400">Demandes de chansons</div>
          <h1 className="font-display text-5xl tracking-tight">Suggère une chanson</h1>
          <p className="text-stone-400 mt-2 max-w-xl">
            Une chanson te manque ? Suggère-la — Maestro l'ajoutera si elle rejoint le catalogue.
          </p>
        </div>

        {/* Submit form */}
        <form onSubmit={submit} className="card-stone p-5 mb-8 rise" data-testid="request-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input
              data-testid="request-title"
              className="input-stone"
              placeholder="Titre de la chanson"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
            />
            <input
              data-testid="request-artist"
              className="input-stone"
              placeholder="Artiste"
              value={form.artist}
              onChange={e => setForm({ ...form, artist: e.target.value })}
            />
          </div>
          <textarea
            data-testid="request-note"
            rows={2}
            className="input-stone mt-3"
            placeholder="Message (optionnel) : pourquoi tu veux apprendre cette chanson ?"
            value={form.note}
            onChange={e => setForm({ ...form, note: e.target.value })}
          />
          <div className="mt-3 flex justify-end">
            <button data-testid="request-submit" disabled={saving} className="btn-primary flex items-center gap-2">
              <Send size={16} /> Envoyer la demande
            </button>
          </div>
        </form>

        {/* Tabs (admin sees two, users just theirs) */}
        {isAdmin && (
          <div className="flex gap-2 mb-4 border-b border-stone-800">
            <button
              onClick={() => setTab('admin')}
              data-testid="tab-admin-requests"
              className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${tab === 'admin' ? 'border-orange-600 text-stone-100' : 'border-transparent text-stone-400 hover:text-stone-200'}`}
            >Toutes les demandes ({all.length})</button>
            <button
              onClick={() => setTab('mine')}
              data-testid="tab-my-requests"
              className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${tab === 'mine' ? 'border-orange-600 text-stone-100' : 'border-transparent text-stone-400 hover:text-stone-200'}`}
            >Mes demandes ({mine.length})</button>
          </div>
        )}

        {tab === 'mine' ? (
          <RequestList items={mine} onDelete={remove} emptyLabel="Tu n'as encore envoyé aucune demande." />
        ) : (
          <div>
            {pending.length > 0 && (
              <div className="mb-6">
                <div className="overline text-orange-300 mb-2">À traiter ({pending.length})</div>
                <RequestList items={pending} onApprove={id => resolve(id, 'approved')} onReject={id => resolve(id, 'rejected')} onDelete={remove} admin />
              </div>
            )}
            <div>
              <div className="overline text-stone-500 mb-2">Historique ({resolved.length})</div>
              <RequestList items={resolved} onDelete={remove} admin emptyLabel="Rien à afficher." />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function RequestList({ items, onApprove, onReject, onDelete, admin, emptyLabel = 'Aucune demande.' }) {
  if (!items || items.length === 0) {
    return <div className="card-stone p-6 text-stone-500 text-sm">{emptyLabel}</div>;
  }
  return (
    <div className="space-y-3">
      {items.map(r => {
        const meta = STATUS_META[r.status] || STATUS_META.pending;
        const Icon = meta.icon;
        return (
          <div key={r.id} data-testid={`request-${r.id}`} className="card-stone p-4 flex gap-4 items-start">
            <div className="w-11 h-11 rounded-xl bg-stone-800 flex items-center justify-center shrink-0">
              <Music4 size={20} className="text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display text-lg truncate">{r.title}</div>
                <div className="text-stone-400 text-sm truncate">— {r.artist}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${meta.color}`}>
                  <Icon size={11} /> {meta.label}
                </span>
              </div>
              {admin && (
                <div className="text-xs text-stone-500 mt-1">
                  Par <span className="text-stone-300">{r.user_name}</span> · <span className="text-stone-500">{r.user_email}</span>
                </div>
              )}
              {r.note && <div className="text-sm text-stone-400 mt-2 italic">« {r.note} »</div>}
              {r.admin_reply && (
                <div className="text-xs text-stone-400 mt-2">
                  <span className="text-orange-300 font-medium">Réponse admin :</span> {r.admin_reply}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              {admin && r.status === 'pending' && (
                <>
                  <button data-testid={`approve-${r.id}`} onClick={() => onApprove(r.id)} className="btn-ghost text-xs flex items-center gap-1 !py-1 !px-3">
                    <CheckCircle2 size={13} className="text-emerald-400" /> Approuver
                  </button>
                  <button data-testid={`reject-${r.id}`} onClick={() => onReject(r.id)} className="btn-ghost text-xs flex items-center gap-1 !py-1 !px-3">
                    <XCircle size={13} className="text-red-400" /> Refuser
                  </button>
                </>
              )}
              <button data-testid={`delete-request-${r.id}`} onClick={() => onDelete(r.id)} className="btn-ghost text-xs flex items-center gap-1 !py-1 !px-3 text-stone-500">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
